import React, { useEffect, useState } from "react";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  Card,
  Typography,
  Modal,
  Pagination,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Grid,
  CircularProgress,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch,
  InputAdornment,
  Stack,
  Button,
  Tooltip,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import JumboSearch from "@jumbo/components/JumboSearch";
import Div from "@jumbo/shared/Div/Div";
import {
  deleteDSOKOrder,
  getBEUrl,
  getCustomer,
  getDSOKOrders,
  sendEkasaPrintCopy,
  sendEkasaRegister,
  sendOrderToExternalSystem,
  setBackendDomain,
} from "app/services/api/management";
import DSOKOrderForm from "./DSOKOrderForm";
import OrderItem from "./OrderItem";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { useSnackbar } from "notistack";
import ClearIcon from "@mui/icons-material/Clear";
import jwtAuthAxiosKiosk from "app/services/auth/jwtAuthKiosk";
import { useSocket } from "app/providers/SocketProvider";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DSOK_ORDER_STATUSSES } from "app/utils/constants/settings";
import { isAfterDate, isBeforeDate } from "app/utils/timeHelpers";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PublicIcon from "@mui/icons-material/Public";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import {
  DemoIcon,
  ErrorIcon,
  OkIcon,
  UserCancelIcon,
} from "./orderStatusIcons";
import { isExternalOrder } from "./externalOrderHelpers";

const defaultFilter = {
  device: "",
  dateFrom: null,
  dateTo: null,
  excludeDemoOrders: false,
  orderStatus: "",
  paymentMethod: "",
  source: "",
};

const SOURCE_OPTIONS = [
  { value: "kiosk", labelKey: "sourceKiosk", defaultLabel: "Kiosk" },
  { value: "external", labelKey: "sourceExternal", defaultLabel: "Externá" },
];

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: { md: "70vw", xs: "95vw" },
  maxHeight: "100%",
  overflow: "scroll",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: { md: 4, xs: 1 },
};

const DEFAULT_ITEMS_PER_PAGE = 20;
const FILTER_STORAGE_KEY = "dsokOrders.filter";
const ITEMS_PER_PAGE_STORAGE_KEY = "dsokOrders.itemsPerPage";

const CardIcon = () => <CreditCardIcon sx={{ color: "#6B7280" }} />;
const CashIcon = () => <LocalAtmIcon sx={{ color: "#6B7280" }} />;
const ReversedIcon = () => <MoneyOffIcon sx={{ color: "#ED6C02" }} />;
const WebIcon = () => <PublicIcon sx={{ color: "#6B7280" }} />;
const AlreadyPaidIcon = () => <PointOfSaleIcon sx={{ color: "#6B7280" }} />;

const PAYMENT_METHOD_OPTIONS = [
  { value: "card", labelKey: "card", defaultLabel: "Karta (terminál)" },
  { value: "cash", labelKey: "cash", defaultLabel: "Hotovosť" },
  { value: "alreadyPaid", labelKey: "alreadyPaid", defaultLabel: "POS (uhradené)" },
  // QR web / online (Besteron) card payment — orders store paymentMethod "online".
  { value: "online", labelKey: "web", defaultLabel: "Web platba" },
  { value: "free", labelKey: "free", defaultLabel: "Zadarmo" },
];

const DSOKOrders = () => {
  const { getAuthUser } = useJumboAuth();
  const authUser = getAuthUser() || {};
  const { customer: { url, id } = {} } = authUser;

  const { socket, connect, unsubscribe, subscribe } = useSocket();
  const { t } = useTranslation();
  const [searchName, setSearchName] = useState();
  const [orderModal, setOrderModal] = useState();
  const [page, setPage] = useState(1);
  // Manual communication actions (send order to external system / eKasa registration)
  // are gated behind a shared code (REACT_APP_ORDER_RESEND_CODE) so they cannot be
  // triggered accidentally. `action` selects which action runs once the code matches.
  const [resendCodeDialog, setResendCodeDialog] = useState({
    open: false,
    orderId: null,
    action: null,
  });
  const [resendCodeValue, setResendCodeValue] = useState("");
  const [resendCodeError, setResendCodeError] = useState(false);
  // eKasa: ak doklad v Portose už existuje, vytlačiť jeho kópiu.
  const [ekasaPrintCopy, setEkasaPrintCopy] = useState(false);
  // eKasa: admin potvrdzuje, že doklad v eKase neexistuje → registrovať aj bez overenia
  // (backend to použije len keď overenie v Portose nie je možné, napr. objednávky spred 28.09.2026).
  const [ekasaForce, setEkasaForce] = useState(false);
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [orders, setOrders] = useState([]);
  const [defaultOrders, setDefaultOrders] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [devices, setDevices] = useState([]);
  const [filter, setFilter] = useState(defaultFilter);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [pageSizeHydrated, setPageSizeHydrated] = useState(false);
  const [totalOrders, setTotalOrders] = useState(null);
  const [totalGrandTotal, setTotalGrandTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState();
  const customerUrl = url || "";

  // Use customerId from localStorage or fallback to auth user ID
  const [customerId, setCustomerId] = useState(id);
  const [dsokSettings, setDsokSettings] = useState(null);

  // Load customerId and dsokSettings from localStorage on mount
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      setDsokSettings(user?.customer?.dsokSettings);
      const localStorageCustomerId = user?.customerId;
      if (localStorageCustomerId) {
        setCustomerId(localStorageCustomerId);
      }
    } catch (error) {
      console.error("Error parsing dsokSettings from localStorage:", error);
    }
  }, []);

  const customerUISettings = dsokSettings?.customer?.customerUISettings;

  const orderManagementStatus = dsokSettings?.orderManagement?.status;
  const orderNotificationsEnabled =
    dsokSettings?.settings?.orderNotifications?.isActive === true;
  const enablePaymentStatus = customerUISettings?.enablePayment;
  const enablePrintOrEkasaStatus = customerUISettings?.enablePrintOrEkasaStatus;
  const enableClientsInList = customerUISettings?.enableClientsInList;

  let currencyCode = "";
  switch (dsokSettings?.customer?.currencyCode) {
    case "EUR":
      currencyCode = "€";
      break;
    case "USD":
      currencyCode = "$";
      break;
    case "Kč":
      currencyCode = "Kč";
      break;
    default:
      currencyCode = "€";
      break;
  }

  const loadOrders = async () => {
    setLoading(true);
    const offset = (page - 1) * itemsPerPage;
    const serializeDateTime = (d, endOfDay = false) => {
      if (!d) return undefined;
      const date = new Date(d);
      if (endOfDay) {
        date.setHours(23, 59, 59, 0);
      } else {
        date.setHours(0, 0, 0, 0);
      }
      const pad = (n) => String(n).padStart(2, "0");
      const yyyy = date.getFullYear();
      const mm = pad(date.getMonth() + 1);
      const dd = pad(date.getDate());
      const hh = pad(date.getHours());
      const mi = pad(date.getMinutes());
      const ss = pad(date.getSeconds());
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    };
    const hasFrom = !!filter.dateFrom;
    const hasTo = !!filter.dateTo;
    const dateParams =
      hasFrom && hasTo
        ? {
            dateFrom: serializeDateTime(filter.dateFrom, false),
            dateTo: serializeDateTime(filter.dateTo, true),
          }
        : {};
    const response = await getDSOKOrders({
      limit: itemsPerPage,
      offset,
      ...dateParams,
      ...(filter?.device ? { device: filter.device } : {}),
      ...(filter?.orderStatus ? { orderStatus: filter.orderStatus } : {}),
      ...(filter?.paymentMethod ? { paymentMethod: filter.paymentMethod } : {}),
      includeMeta: true,
    });
    const { orders: ordersResponse, pagination } = response || {};
    if (pagination && typeof pagination.total === "number") {
      setTotalOrders(pagination.total);
      setTotalGrandTotal(
        typeof pagination.totalGrandTotal === "number"
          ? pagination.totalGrandTotal
          : null
      );
    }
    {
      let orderArr = [];
      if (Array.isArray(ordersResponse)) {
        orderArr = ordersResponse.slice();
      } else if (ordersResponse && typeof ordersResponse === "object") {
        orderArr = Object.keys(ordersResponse || {}).map((id) => ({
          ...ordersResponse[id],
        }));
      }
      orderArr = orderArr.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      // Normalize local filter range to start/end of day for client-side filtering
      const startOfDay = (d) => {
        const x = new Date(d);
        x.setHours(0, 0, 0, 0);
        return x;
      };
      const endOfDay = (d) => {
        const x = new Date(d);
        x.setHours(23, 59, 59, 999);
        return x;
      };
      const filterFrom = filter.dateFrom ? startOfDay(filter.dateFrom) : null;
      const filterTo = filter.dateTo ? endOfDay(filter.dateTo) : null;

      let onlyTodayOrders = false;
      if (filterFrom && filterTo) {
        const fromDate = new Date(filterFrom).toDateString();
        const toDate = new Date(filterTo).toDateString();
        if (fromDate === toDate) {
          onlyTodayOrders = true;
        }
      }
      const filteredOrders = orderArr.filter((order) => {
        const orderPaymentMethod = String(
          order?.payment?.method ?? order?.paymentMethod ?? ""
        ).toLowerCase();
        const filterPaymentMethod = filter.paymentMethod
          ? filter.paymentMethod.toLowerCase()
          : "";
        const orderIsExternal = isExternalOrder(order);
        const sourceMatches = filter.source
          ? filter.source === "external"
            ? orderIsExternal
            : !orderIsExternal
          : true;
        let conditions = [];
        if (onlyTodayOrders) {
          conditions = [
            !order.isDeleted,
            filter.device ? order.kioskUUID === filter.device : true,
            sourceMatches,
            filter.orderStatus
              ? order.orderStatus === filter.orderStatus
              : true,
            filterPaymentMethod
              ? orderPaymentMethod === filterPaymentMethod
              : true,
            filter.excludeDemoOrders ? !order.demo : true,
            filterFrom ? isAfterDate(order.createdAt, filterFrom) : true,
          ];
        } else {
          conditions = [
            !order.isDeleted,
            filter.device ? order.kioskUUID === filter.device : true,
            sourceMatches,
            filter.orderStatus
              ? order.orderStatus === filter.orderStatus
              : true,
            filterPaymentMethod
              ? orderPaymentMethod === filterPaymentMethod
              : true,
            filter.excludeDemoOrders ? !order.demo : true,
            filterFrom ? isAfterDate(order.createdAt, filterFrom) : true,
            filterTo ? isBeforeDate(order.createdAt, filterTo) : true,
          ];
        }

        return conditions.every(Boolean);
      });
      const searchedOrders = searchName
        ? filteredOrders.filter((order) => {
            const conditions = [
              `${order?.pickupCode}`?.includes(searchName),
              `${order?.grandTotal}`?.includes(searchName),
              `${order?.createdAt}`?.includes(searchName),
              `${order?.orderStatus}`?.includes(searchName),
              `${order?.locator}`?.includes(searchName),
              `${order?.externalOrderId}`?.includes(searchName),
            ];
            return conditions.some(Boolean);
          })
        : filteredOrders;
      setOrders(searchedOrders || []);
      setDefaultOrders(orderArr || []);
      // Update pageCount dynamically (unknown total). If fewer than limit, this is the last page.
      const received = orderArr.length;
      const nextCount = received < itemsPerPage ? page : page + 1;
      setPageCount(Math.max(1, nextCount));
    }
    setLoading(false);
  };

  const handleUpdate = (message) => {
    console.log(
      "DSOKOrders: WebSocket event received on admin/dsokOrder:",
      message,
    );
    if (message === "refresh") {
      console.log("DSOKOrders: Refreshing orders due to WebSocket update");
      loadOrders();
    }
  };

  useEffect(() => {
    const devices = Object.values(dsokSettings?.customer?.devices || {});
    if (devices.length) {
      setDevices(devices);
      devices.forEach((device) => {
        const state = device?.ekasa?.print.paperState || "";
        if (state === "NearEnd") {
          const message = t("pages.dsokOrders.paperNearEnd", {
            deviceName: device?.deviceName,
          });
          enqueueSnackbar(message, {
            variant: "warning",
            key: device?.uuid || "",
            preventDuplicate: true,
            anchorOrigin: { horizontal: "right", vertical: "top" },
            persist: true,
            action: (
              <IconButton onClick={() => closeSnackbar(device?.uuid)}>
                <CloseIcon />
              </IconButton>
            ),
          });
        } else if (state === "Empty") {
          const message = t("pages.dsokOrders.paperEmpty", {
            deviceName: device?.deviceName,
          });
          enqueueSnackbar(message, {
            variant: "error",
            key: device?.uuid || "",
            preventDuplicate: true,
            anchorOrigin: { horizontal: "right", vertical: "top" },
            persist: true,
            action: (
              <IconButton onClick={() => closeSnackbar(device?.uuid)}>
                <CloseIcon />
              </IconButton>
            ),
          });
        } else {
          device?.uuid && closeSnackbar(device?.uuid);
        }
      });
    }
  }, [dsokSettings?.customer?.devices]);
  // WebSocket connection
  useEffect(() => {
    const baseURL = getBEUrl().replace(/\/$/, ""); // Remove trailing slash
    const wsURL = baseURL
      .replace(/^https:\/\//, "wss://")
      .replace(/^http:\/\//, "ws://"); // Convert to appropriate WebSocket protocol
    connect(wsURL);
  }, [connect]);
  // WebSocket subscription for order changes
  useEffect(() => {
    if (socket?.active && customerId) {
      const channel = `admin/dsokOrder/${customerId}`;
      console.log("DSOKOrders: Subscribing to WebSocket channel:", channel);
      subscribe(channel, handleUpdate);

      return () => {
        console.log(
          "DSOKOrders: Unsubscribing from WebSocket channel:",
          channel,
        );
        unsubscribe(channel, handleUpdate);
      };
    }
  }, [socket?.active, customerId, id, subscribe, unsubscribe]);
  // When search changes, reset to first page; unified loader handles fetching
  useEffect(() => {
    setPage(1);
  }, [searchName]);

  const loadCustomer = async () => {
    await getCustomer(customerUrl).then((customer) => setCustomer(customer));
  };

  const updateFilter = (newFilter) => {
    setFilter({ ...filter, ...newFilter });
  };

  // Load saved filter on mount (persist across refresh)
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY));
      if (saved && typeof saved === "object") {
        setFilter({
          ...defaultFilter,
          ...saved,
          dateFrom: saved.dateFrom ? new Date(saved.dateFrom) : null,
          dateTo: saved.dateTo ? new Date(saved.dateTo) : null,
        });
      }
    } catch (e) {
      // ignore parsing errors
    }
    setFiltersHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved items-per-page on mount
  useEffect(() => {
    try {
      const saved = Number(localStorage.getItem(ITEMS_PER_PAGE_STORAGE_KEY));
      if ([20, 30, 50, 100].includes(saved)) {
        setItemsPerPage(saved);
      }
    } catch (e) {
      // ignore parsing errors
    }
    setPageSizeHydrated(true);
  }, []);

  // Persist filter whenever it changes
  useEffect(() => {
    const toSave = {
      ...filter,
      dateFrom: filter.dateFrom
        ? new Date(filter.dateFrom).toISOString()
        : null,
      dateTo: filter.dateTo ? new Date(filter.dateTo).toISOString() : null,
    };
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) {
      // ignore storage errors
    }
  }, [filter]);

  // Persist items-per-page whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(ITEMS_PER_PAGE_STORAGE_KEY, String(itemsPerPage));
    } catch (e) {
      // ignore storage errors
    }
  }, [itemsPerPage]);

  // When filters change, fetch immediately if on page 1; otherwise reset to page 1
  useEffect(() => {
    if (!filtersHydrated || !pageSizeHydrated) return;
    if (page !== 1) {
      setPage(1);
    } else {
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.device,
    filter.dateFrom,
    filter.dateTo,
    filter.orderStatus,
    filter.paymentMethod,
    filter.excludeDemoOrders,
    filtersHydrated,
    pageSizeHydrated,
  ]);

  // Unified loader: fetch after hydration when page or page size changes
  useEffect(() => {
    if (!filtersHydrated || !pageSizeHydrated) return;
    loadOrders();
  }, [page, itemsPerPage, filtersHydrated, pageSizeHydrated]);

  // Remove effect-based page reset to avoid double fetch; handle in onChange instead

  useEffect(() => {
    loadCustomer();
  }, []);

  /**
   * Hláška a variant snackbaru podľa odpovede backendu (/dsok/order/ekasa-register).
   * body = telo úspešnej odpovede alebo response.data chyby.
   */
  const getEkasaRegisterFeedback = (body, failed) => {
    const ns = "pages.dsokOrders.ekasaRegister";
    const statusCode = body?.statusCode;
    if (!failed) {
      if (statusCode === "ekasaAlreadyRegistered") {
        let message = t(`${ns}.alreadyRegistered`);
        if (body?.printCopy === "ok") message += ` ${t(`${ns}.printCopyOk`)}`;
        if (body?.printCopy === "failed") message += ` ${t(`${ns}.printCopyFailed`)}`;
        return { message, variant: body?.printCopy === "failed" ? "warning" : "success" };
      }
      return {
        message: body?.forced ? t(`${ns}.registeredForced`) : t(`${ns}.registered`),
        variant: "success",
      };
    }
    switch (statusCode) {
      case "ekasaCannotVerify": {
        const reason = [
          "dlaNotSupported",
          "dlaNotConnected",
          "missingExternalId",
          "missingCashRegisterCode",
        ].includes(body?.reason)
          ? body.reason
          : "unknown";
        // Pri pripojenej DLA sa dá registrovať s potvrdením, že doklad neexistuje → poradiť.
        const hint = reason !== "dlaNotConnected" ? ` ${t(`${ns}.forceHint`)}` : "";
        return {
          message: `${t(`${ns}.cannotVerify`)} ${t(`${ns}.cannotVerifyReason.${reason}`)}${hint}`,
          variant: "warning",
        };
      }
      case "ekasaRegistrationInProgress":
        return { message: t(`${ns}.inProgress`), variant: "info" };
      case "ekasaLookupFailed":
        return {
          message: t(`${ns}.lookupFailed`, { code: body?.error?.code || "—" }),
          variant: "error",
        };
      case "ekasaPrintFail": {
        const detail = [body?.error?.code, body?.error?.message]
          .filter(Boolean)
          .join(": ");
        return {
          message: detail
            ? t(`${ns}.failedWithReason`, { reason: detail })
            : t(`${ns}.failed`),
          variant: "error",
        };
      }
      default:
        return {
          message: `${t("pages.dsokOrders.eKasaRegistration")} ${t(
            "notifications.wasFailed",
          )}`,
          variant: "error",
        };
    }
  };

  const handleEkasaRegister = async (id, options = {}) => {
    setLoading(true);
    hideDialog();
    let feedback;
    try {
      const body = await sendEkasaRegister(id, options);
      feedback = getEkasaRegisterFeedback(body, false);
    } catch (error) {
      feedback = getEkasaRegisterFeedback(error?.response?.data, true);
    }
    // Aj pri chybe backend mohol uložiť nový stav/chybu eKasy → obnoviť zoznam.
    try {
      await loadOrders();
    } catch (_) {
      setLoading(false);
    }
    enqueueSnackbar(feedback.message, {
      variant: feedback.variant,
      autoHideDuration: feedback.variant === "success" ? 5000 : 10000,
    });
  };
  // Tlač kópie eKasa Portos dokladu (SmartKiosk / DLA) — hláška podľa odpovede backendu.
  const handleEkasaPrintCopy = async (id) => {
    hideDialog();
    const ns = "pages.dsokOrders.ekasaCopy";
    let message;
    let variant;
    try {
      await sendEkasaPrintCopy(id, authUser?.email || authUser?.username || null);
      message = t(`${ns}.printed`);
      variant = "success";
    } catch (error) {
      const body = error?.response?.data;
      if (body?.statusCode === "ekasaCopyNotAvailable") {
        const reason = [
          "notRegistered",
          "notPortos",
          "missingExternalId",
          "notDla",
          "missingCashRegisterCode",
          "dlaNotConnected",
          "dlaNotSupported",
        ].includes(body?.reason)
          ? body.reason
          : "unknown";
        message = `${t(`${ns}.notAvailable`)} ${t(`${ns}.reason.${reason}`)}`;
        variant = "warning";
      } else if (body?.statusCode === "ekasaCopyFailed") {
        message = t(`${ns}.failed`, { code: body?.error?.code || "—" });
        variant = "error";
      } else {
        message = t(`${ns}.failed`, { code: error?.response?.status || "—" });
        variant = "error";
      }
    }
    enqueueSnackbar(message, {
      variant,
      autoHideDuration: variant === "success" ? 5000 : 10000,
    });
  };

  const handleResendOrderToExternalSystem = async (id) => {
    setLoading(true);
    hideDialog();
    try {
      await sendOrderToExternalSystem(id);
      await loadOrders();
      const message = `${t("pages.dsokOrders.externalSystemRegistration")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("pages.dsokOrders.externalSystemRegistration")}  ${t(
        "notifications.wasFailed",
      )}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
  };

  const openCodeDialog = (id, action) => {
    setResendCodeValue("");
    setResendCodeError(false);
    setEkasaPrintCopy(false);
    setEkasaForce(false);
    setResendCodeDialog({ open: true, orderId: id, action });
  };

  const closeResendCodeDialog = () => {
    setResendCodeDialog({ open: false, orderId: null, action: null });
    setResendCodeValue("");
    setResendCodeError(false);
  };

  const confirmResendWithCode = () => {
    const expectedCode = process.env.REACT_APP_ORDER_RESEND_CODE;
    if (!resendCodeValue || resendCodeValue !== expectedCode) {
      setResendCodeError(true);
      return;
    }
    const { orderId, action } = resendCodeDialog;
    const printCopy = ekasaPrintCopy;
    const forceWithoutVerification = ekasaForce;
    closeResendCodeDialog();
    if (action === "ekasa") {
      handleEkasaRegister(orderId, {
        printCopy,
        forceWithoutVerification,
        changedBy: authUser?.email || authUser?.username || null,
      });
    } else {
      handleResendOrderToExternalSystem(orderId);
    }
  };

  const handleOrderDelete = async (id) => {
    setLoading(true);
    hideDialog();
    try {
      await deleteDSOKOrder(id);
      await loadOrders();
      const message = `${t("buttons.delete")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("buttons.delete")}  ${t("notifications.wasFailed")}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
  };
  const orderTitle = orderModal?.order?.externalOrderId
    ? orderModal?.order?.orderCode +
      " ( " +
      orderModal?.order?.externalOrderId +
      " )"
    : orderModal?.order?.orderCode;
  return (
    <React.Fragment>
      <Typography variant={"h2"} mb={3}>
        {t("pages.dsokOrders.title")}
      </Typography>
      <Grid container columnSpacing={2} rowGap={2} mb={2}>
        <Grid item md={6} xs={12}>
          <Card sx={{ mb: 0, width: "100%" }}>
            <JumboSearch
              page="DSOKOrders"
              onChange={setSearchName}
              sx={{
                width: "100%",
              }}
              placeholder={t("pages.presentation.searchPresentation")}
            />
          </Card>
        </Grid>
        <Grid item md={6} xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: { md: "flex-end", xs: "center" },
            }}
          >
            <TextField
              select
              size="small"
              sx={{ width: 140 }}
              name={`itemsPerPage`}
              value={itemsPerPage}
              onChange={(e) => {
                const value = Number(e.target.value);
                // Reset to first page and apply new page size
                setItemsPerPage(value);
                setPage(1);
              }}
              label={t("pages.dsokOrders.itemsPerPage")}
              InputLabelProps={{
                shrink: true,
              }}
            >
              {[20, 30, 50, 100].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
            {totalOrders != null && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  ml: 1,
                  whiteSpace: "nowrap",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("pages.dsokOrders.totalOrdersCount", { defaultValue: "Objednávky" })}: <strong>{totalOrders}</strong>
                </Typography>
                {totalGrandTotal != null && (
                  <Typography variant="body2" color="text.secondary">
                    {t("pages.dsokOrders.totalSum", { defaultValue: "Suma" })}: <strong>{totalGrandTotal.toFixed(2)} {currencyCode}</strong>
                  </Typography>
                )}
              </Box>
            )}
            <Pagination
              count={pageCount}
              color="primary"
              size={isMobile ? "small" : "medium"}
              siblingCount={0}
              boundaryCount={1}
              page={page}
              onChange={(_, val) => setPage(val)}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid item md={12} xs={12}>
        <Card sx={{ px: 3, mb: 3 }}>
          <Grid
            container
            columnSpacing={2}
            rowGap={2}
            mb={2}
            justifyContent="center"
          >
            <Grid item md={2} xs={12}>
              <Div sx={{ mb: 0, mt: 2, mx: 0 }}>
                <TextField
                  select
                  sx={{ width: "100%" }}
                  name={`device`}
                  value={filter.device}
                  onChange={(e) => updateFilter({ device: e.target.value })}
                  label={t("pages.dsokOrders.device")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          sx={{ p: 0.5 }}
                          onClick={(e) => updateFilter({ device: "" })}
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                >
                  {devices?.map((device, index) => {
                    return (
                      <MenuItem key={index} value={device.uuid}>
                        {device?.deviceName || device?.uuid}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Div>
            </Grid>
            <Grid item md={2} xs={12}>
              <Div sx={{ mt: 2, mb: 0, mx: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("pages.menus.dateFrom")}
                    sx={{ width: "100%" }}
                    format="dd/MM/yy"
                    slotProps={{
                      textField: {
                        InputLabelProps: { shrink: true },
                        placeholder: "",
                      },
                      actionBar: {
                        actions: ["clear"],
                      },
                    }}
                    onChange={(newValue) =>
                      updateFilter({ dateFrom: newValue })
                    }
                    value={filter.dateFrom}
                  />
                </LocalizationProvider>
              </Div>
            </Grid>
            <Grid item md={2} xs={12}>
              <Div sx={{ mt: 2, mb: 0, mx: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label={t("pages.menus.dateTo")}
                    sx={{ width: "100%" }}
                    format="dd/MM/yy"
                    slotProps={{
                      textField: {
                        InputLabelProps: { shrink: true },
                        placeholder: "",
                      },
                      actionBar: {
                        actions: ["clear"],
                      },
                    }}
                    onChange={(newValue) => updateFilter({ dateTo: newValue })}
                    value={filter.dateTo}
                  />
                </LocalizationProvider>
              </Div>
            </Grid>
            <Grid item md={2} xs={12}>
              <Div sx={{ mb: 0, mt: 2, mx: 0 }}>
                <TextField
                  select
                  sx={{ width: "100%" }}
                  name={`orderStatus`}
                  value={filter.orderStatus}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          sx={{ p: 0.5 }}
                          onClick={(e) => updateFilter({ orderStatus: "" })}
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) =>
                    updateFilter({ orderStatus: e.target.value })
                  }
                  label={t("pages.dsokOrders.orderStatus")}
                >
                  {DSOK_ORDER_STATUSSES?.map((status, index) => {
                    return (
                      <MenuItem key={index} value={status}>
                        {t(`pages.dsokOrders.${status}`)}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Div>
            </Grid>
            <Grid item md={2} xs={12}>
              <Div sx={{ mb: 0, mt: 2, mx: 0 }}>
                <TextField
                  select
                  sx={{ width: "100%" }}
                  name={`paymentMethod`}
                  value={filter.paymentMethod}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          sx={{ p: 0.5 }}
                          onClick={() => updateFilter({ paymentMethod: "" })}
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) =>
                    updateFilter({ paymentMethod: e.target.value })
                  }
                  label={t("pages.dsokOrders.paymentMethodType", { defaultValue: "Spôsob platby" })}
                >
                  {PAYMENT_METHOD_OPTIONS.map(({ value, labelKey, defaultLabel }) => (
                    <MenuItem key={value} value={value}>
                      {t(`pages.dsokOrders.paymentMethods.${labelKey}`, { defaultValue: defaultLabel })}
                    </MenuItem>
                  ))}
                </TextField>
              </Div>
            </Grid>
            <Grid item md={2} xs={12}>
              <Div sx={{ mb: 0, mt: 2, mx: 0 }}>
                <TextField
                  select
                  sx={{ width: "100%" }}
                  name={`source`}
                  value={filter.source}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          sx={{ p: 0.5 }}
                          onClick={() => updateFilter({ source: "" })}
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => updateFilter({ source: e.target.value })}
                  label={t("pages.dsokOrders.external.sourceFilter", {
                    defaultValue: "Zdroj",
                  })}
                >
                  {SOURCE_OPTIONS.map(({ value, labelKey, defaultLabel }) => (
                    <MenuItem key={value} value={value}>
                      {t(`pages.dsokOrders.external.${labelKey}`, {
                        defaultValue: defaultLabel,
                      })}
                    </MenuItem>
                  ))}
                </TextField>
              </Div>
            </Grid>
            <Grid item md={2} xs={12}>
              <Div sx={{ mt: 3, mb: 1, textAlign: "start" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filter.excludeDemoOrders}
                      onChange={(e) =>
                        updateFilter({ excludeDemoOrders: e.target.checked })
                      }
                      name="excludeDemoOrders"
                    />
                  }
                  label={t("pages.dsokOrders.excludeDemoOrders")}
                />
              </Div>
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  pb: 0.5,
                  "&::-webkit-scrollbar": { height: 6 },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("pages.dsokOrders.legend.title")}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <OkIcon /> {t("pages.dsokOrders.legend.ok")}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <ErrorIcon /> {t("pages.dsokOrders.legend.error")}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <DemoIcon /> {t("pages.dsokOrders.legend.demo")}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <UserCancelIcon /> {t("pages.dsokOrders.legend.userCanceled")}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <ReversedIcon />{" "}
                  {t("pages.dsokOrders.legend.reversed", "Stornované")}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <CardIcon />{" "}
                  {t("pages.dsokOrders.paymentMethods.card", {
                    defaultValue: "Card",
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <CashIcon />{" "}
                  {t("pages.dsokOrders.paymentMethods.cash", {
                    defaultValue: "Cash",
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <WebIcon />{" "}
                  {t("pages.dsokOrders.paymentMethods.web", {
                    defaultValue: "Web platba",
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  display="inline-flex"
                  alignItems="center"
                  gap={0.75}
                >
                  <AlreadyPaidIcon />{" "}
                  {t("pages.dsokOrders.paymentMethods.alreadyPaid", {
                    defaultValue: "POS (uhradené)",
                  })}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      {loading ? (
        <CircularProgress sx={{ m: "5px 50%" }} />
      ) : (
        <>
          {orders?.length ? (
            <>
              {orders.map((order, index) => (
                <OrderItem
                  customer={customer}
                  key={index}
                  item={order}
                  currencyCode={currencyCode}
                  onPreview={() =>
                    setOrderModal({ type: "previewOrder", order })
                  }
                  onEkasaRegister={(id) => openCodeDialog(id, "ekasa")}
                  onEkasaPrintCopy={(id) =>
                    showDialog({
                      variant: "confirm",
                      title: t("pages.dsokOrders.ekasaCopy.confirm", {
                        number: order?.internalOrderNumber || "",
                      }),
                      onYes: () => handleEkasaPrintCopy(id),
                      onNo: () => hideDialog(),
                    })
                  }
                  onResendOrderToExternalSystem={(id) =>
                    openCodeDialog(id, "external")
                  }
                  onDelete={(id) =>
                    showDialog({
                      variant: "confirm",
                      title: t("widgets.confirmDialog.areYouSureToDelete"),
                      onYes: () => handleOrderDelete(id),
                      onNo: () => hideDialog(),
                    })
                  }
                  searchName={searchName}
                />
              ))}
            </>
          ) : (
            <JumboListNoDataPlaceHolder />
          )}
        </>
      )}
      <Modal open={orderModal !== undefined} onClose={() => setOrderModal()}>
        <Div sx={modalStyle}>
          <DSOKOrderForm
            title={`#${orderTitle || ""} ${
              orderModal?.order?.demo ? " - DEMO" : ""
            }`}
            order={orderModal?.order}
            onCancel={() => setOrderModal()}
            orders={defaultOrders}
            currencyCode={currencyCode}
            orderManagementStatus={orderManagementStatus}
            enablePaymentStatus={enablePaymentStatus}
            enablePrintOrEkasaStatus={enablePrintOrEkasaStatus}
            enableClientsInList={enableClientsInList}
            lineOfBusiness={dsokSettings?.customer?.lineOfBusiness}
            orderNotificationsEnabled={orderNotificationsEnabled}
            onStatusChanged={() => loadOrders()}
            changedBy={authUser?.email || authUser?.username || "admin"}
          />
          <IconButton
            onClick={() => setOrderModal()}
            sx={{ position: "absolute", right: 10, top: 15 }}
          >
            <ClearIcon />
          </IconButton>
        </Div>
      </Modal>
      <Modal open={resendCodeDialog.open} onClose={closeResendCodeDialog}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 360,
            maxWidth: "90%",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography variant="h5" sx={{ mb: 2 }}>
            {resendCodeDialog.action === "ekasa"
              ? t("pages.dsokOrders.eKasaRegistration")
              : t("pages.dsokOrders.externalSystemRegistration")}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            size="small"
            label={t("pages.dsokOrders.resendCode")}
            value={resendCodeValue}
            error={resendCodeError}
            helperText={
              resendCodeError ? t("pages.dsokOrders.wrongResendCode") : ""
            }
            onChange={(e) => {
              setResendCodeValue(e.target.value);
              if (resendCodeError) setResendCodeError(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmResendWithCode();
            }}
          />
          {resendCodeDialog.action === "ekasa" && (
            <Tooltip arrow title={t("pages.dsokOrders.ekasaRegister.printCopyIfExistsHelp")}>
              <Box component="span" sx={{ display: "inline-flex", mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ekasaPrintCopy}
                      onChange={(e) => setEkasaPrintCopy(e.target.checked)}
                    />
                  }
                  label={t("pages.dsokOrders.ekasaRegister.printCopyIfExists")}
                />
              </Box>
            </Tooltip>
          )}
          {resendCodeDialog.action === "ekasa" && (
            <>
              <Tooltip arrow title={t("pages.dsokOrders.ekasaRegister.forceHelp")}>
                <Box component="span" sx={{ display: "inline-flex", mt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="warning"
                        checked={ekasaForce}
                        onChange={(e) => setEkasaForce(e.target.checked)}
                      />
                    }
                    label={t("pages.dsokOrders.ekasaRegister.force")}
                  />
                </Box>
              </Tooltip>
              {ekasaForce && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t("pages.dsokOrders.ekasaRegister.forceWarning")}
                </Alert>
              )}
            </>
          )}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button variant="outlined" onClick={closeResendCodeDialog}>
              {t("buttons.cancel")}
            </Button>
            <Button variant="contained" onClick={confirmResendWithCode}>
              {t("buttons.confirm")}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </React.Fragment>
  );
};

export default DSOKOrders;
